import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AutocompleteComponents.css';

// ========================================
// UTILITY FUNCTIONS
// ========================================

/**
 * Evidenzia il testo cercato all'interno del risultato
 */
const highlightText = (text, searchTerm) => {
  if (!searchTerm.trim()) return text;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="highlight">{part}</mark>
    ) : (
      <span key={index}>{part}</span>
    )
  );
};

/**
 * 🆕 NUOVO: Funzione per ricerca intelligente nome/cognome
 * Permette di cercare sia "Mario Rossi" che "Rossi Mario"
 */
const smartNameSearch = (user, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) return false;
  
  const search = searchTerm.toLowerCase().trim();
  const nome = (user.nome || '').toLowerCase();
  const cognome = (user.cognome || '').toLowerCase();
  
  // 1. Ricerca semplice: cerca in nome O cognome separatamente
  if (nome.includes(search) || cognome.includes(search)) {
    return true;
  }
  
  // 2. Ricerca combinata: "nome cognome"
  const nomeCompleto = `${nome} ${cognome}`;
  if (nomeCompleto.includes(search)) {
    return true;
  }
  
  // 3. Ricerca invertita: "cognome nome"
  const nomeInvertito = `${cognome} ${nome}`;
  if (nomeInvertito.includes(search)) {
    return true;
  }
  
  // 4. Ricerca con spazi multipli: es. "mario   rossi"
  const searchWords = search.split(/\s+/).filter(w => w.length > 0);
  if (searchWords.length > 1) {
    // Tutte le parole devono essere presenti in nome O cognome
    const allWordsMatch = searchWords.every(word => 
      nome.includes(word) || cognome.includes(word)
    );
    if (allWordsMatch) {
      return true;
    }
  }
  
  return false;
};

/**
 * Hook per rilevare click esterni al componente
 */
const useClickOutside = (ref, callback) => {
  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ref, callback]);
};

/**
 * Hook per rilevare se siamo su mobile
 */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// ========================================
// COMPONENTE BASE: AutocompleteBase
// ========================================

const AutocompleteBase = ({
  items = [],
  value,
  onChange,
  onSelect,
  placeholder = "Cerca...",
  label,
  displayKey = 'nome',
  searchKeys = ['nome'],
  customSearchFunction = null, // 🆕 NUOVO: funzione di ricerca personalizzata
  renderItem,
  renderSelectedItem,
  noResultsText = "Nessun risultato trovato",
  maxResults = 10,
  required = false,
  disabled = false,
  icon = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hasValidSelection, setHasValidSelection] = useState(false);
  
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const isMobile = useIsMobile();

  // Chiudi dropdown quando si clicca fuori
  useClickOutside(containerRef, () => setIsOpen(false));

  // Filtra gli items in base alla ricerca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredItems([]);
      setIsOpen(false);
      return;
    }

    // Non filtrare se c'è una selezione valida
    if (hasValidSelection) {
      setFilteredItems([]);
      setIsOpen(false);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    
    // 🆕 NUOVO: Usa funzione di ricerca personalizzata se fornita
    const filtered = customSearchFunction
      ? items.filter(item => customSearchFunction(item, searchTerm))
      : items.filter(item => {
          return searchKeys.some(key => {
            const value = item[key];
            if (typeof value === 'string') {
              return value.toLowerCase().includes(searchLower);
            }
            return false;
          });
        });

    setFilteredItems(filtered.slice(0, maxResults));
    // 🆕 MODIFICATO: Apri sempre il dropdown quando c'è del testo (anche se nessun risultato)
    setIsOpen(true);
    setHighlightedIndex(-1);
  }, [searchTerm, maxResults, hasValidSelection]);
  // ✅ FIX LOOP INFINITO: Rimossi items, searchKeys e customSearchFunction dalle dipendenze
  // Questi vengono ricreati ad ogni render del componente padre causando il loop

 // Trova l'item selezionato
useEffect(() => {
  if (value) {
    const item = items.find(i => i.id === value);
    
    // ✅ FIX: Aggiorna solo se l'item è cambiato
    if (selectedItem?.id !== item?.id) {
      setSelectedItem(item);
    }
    
    if (item && !searchTerm) {
      const display = renderSelectedItem 
        ? renderSelectedItem(item) 
        : item[displayKey];
      
      // ✅ FIX CRITICO: Setta searchTerm solo se diverso per evitare loop
      if (searchTerm !== display) {
        setSearchTerm(display);
        setHasValidSelection(true);
      }
    }
  } else {
    if (selectedItem !== null) {
      setSelectedItem(null);
    }
    // 🆕 MODIFICATO: Non cancellare più l'input automaticamente
    // if (!isOpen) setSearchTerm('');
    if (hasValidSelection) {
      setHasValidSelection(false);
    }
  }
}, [value, displayKey, searchTerm, hasValidSelection, selectedItem]);
// ✅ FIX: Rimosso items e renderSelectedItem dalle dipendenze
// ✅ FIX: Aggiunti searchTerm, hasValidSelection e selectedItem

  // Gestione input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setHasValidSelection(false);
    if (onChange) onChange(newValue);
    
    // Reset selezione se l'utente sta digitando
    if (selectedItem) {
      onSelect(null);
    }
  };

  // Gestione selezione item
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    const display = renderSelectedItem 
      ? renderSelectedItem(item) 
      : item[displayKey];
    setSearchTerm(display);
    setIsOpen(false);
    setHighlightedIndex(-1);
    setHasValidSelection(true);
    
    if (onSelect) onSelect(item);
    
    // Rimuovi focus dall'input
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  // Gestione tastiera
  const handleKeyDown = (e) => {
    if (!isOpen || filteredItems.length === 0) {
      if (e.key === 'ArrowDown' && searchTerm.trim() && !hasValidSelection) {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredItems.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
          handleSelectItem(filteredItems[highlightedIndex]);
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (inputRef.current) {
          inputRef.current.blur();
        }
        break;
      
      default:
        break;
    }
  };

  // Scroll automatico per l'item evidenziato
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  // Gestione focus
  const handleFocus = () => {
    if (searchTerm.trim() && filteredItems.length > 0 && !hasValidSelection) {
      setIsOpen(true);
    }
  };

  // Funzione per cancellare la selezione
  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm('');
    setSelectedItem(null);
    setIsOpen(false);
    setHasValidSelection(false);
    if (onSelect) onSelect(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div 
      className={`autocomplete-container ${disabled ? 'disabled' : ''} ${isMobile ? 'mobile' : ''}`}
      ref={containerRef}
    >
      {label && (
        <label className="autocomplete-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      
      <div className="autocomplete-input-wrapper">
        {icon && <span className="autocomplete-icon">{icon}</span>}
        
        <input
          ref={inputRef}
          type="text"
          className={`autocomplete-input ${selectedItem ? 'has-value' : ''}`}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-label={label || placeholder}
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="autocomplete-dropdown"
        />
        
        {searchTerm && (
          <button
            type="button"
            className="autocomplete-clear"
            onClick={handleClear}
            aria-label="Cancella"
            tabIndex={-1}
          >
            ✕
          </button>
        )}
      </div>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className="autocomplete-dropdown"
          id="autocomplete-dropdown"
          role="listbox"
        >
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <div
                key={item.id}
                className={`autocomplete-item ${index === highlightedIndex ? 'highlighted' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectItem(item);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                role="option"
                aria-selected={index === highlightedIndex}
              >
                {renderItem ? (
                  renderItem(item, searchTerm)
                ) : (
                  <span>{highlightText(item[displayKey], searchTerm)}</span>
                )}
              </div>
            ))
          ) : (
            <div className="autocomplete-no-results">
              {noResultsText} "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ========================================
// COMPONENTI SPECIFICI
// ========================================

/**
 * 🆕 MIGLIORATO: Autocomplete per la selezione del Personale
 * - Ricerca SOLO per nome e cognome (NO email)
 * - Ricerca intelligente: funziona con "Mario Rossi" o "Rossi Mario"
 * - Migliore gestione omonimia con badge ruolo sempre visibile
 */
export const PersonaleAutocomplete = ({
  users = [],
  selectedPersona,
  setSelectedPersona,
  onSelect,
  placeholder = "Cerca per nome o cognome...",
  label = "Personale",
  required = false
}) => {
  return (
    <AutocompleteBase
      items={users}
      value={selectedPersona}
      onSelect={(user) => {
        if (setSelectedPersona) setSelectedPersona(user?.id || null);
        if (onSelect) onSelect(user);
      }}
      placeholder={placeholder}
      label={label}
      required={required}
      displayKey="nome"
      customSearchFunction={smartNameSearch} // 🆕 Usa ricerca intelligente
      renderSelectedItem={(user) => `${user.nome} ${user.cognome}`}
      renderItem={(user, searchTerm) => (
        <div className="user-item">
          <div className="user-avatar">
            {user.nome?.[0]}{user.cognome?.[0]}
          </div>
          <div className="user-info">
            <div className="user-name">
              {highlightText(`${user.nome} ${user.cognome}`, searchTerm)}
            </div>
            {/* 🆕 MODIFICATO: Email rimossa, badge ruolo sempre visibile */}
            {user.ruolo && (
              <span className="user-badge">{user.ruolo}</span>
            )}
            {/* 🆕 NUOVO: Mostra incarico per differenziare omonimi */}
            {user.incarico && (
              <div className="user-incarico">
                {user.incarico}
              </div>
            )}
          </div>
        </div>
      )}
      noResultsText="Nessun utente trovato per"
    />
  );
};

/**
 * Autocomplete per la selezione dei Mezzi
 */
export const MezziAutocomplete = ({
  mezzi = [],
  selectedMezzo,
  setSelectedMezzo,
  onSelect,
  placeholder = "Cerca mezzo per nome o targa...",
  label = "Mezzo",
  required = false
}) => {
  return (
    <AutocompleteBase
      items={mezzi}
      value={selectedMezzo}
      onSelect={(mezzo) => {
        if (setSelectedMezzo) setSelectedMezzo(mezzo?.id || null);
        if (onSelect) onSelect(mezzo);
      }}
      placeholder={placeholder}
      label={label}
      required={required}
      displayKey="nomeMezzo"
      searchKeys={['nomeMezzo', 'nome', 'targa']}
      renderSelectedItem={(mezzo) => mezzo.nomeMezzo || mezzo.nome}
      renderItem={(mezzo, searchTerm) => (
        <div className="mezzo-item">
          <div className="mezzo-icon">🚗</div>
          <div className="mezzo-info">
            <div className="mezzo-name">
              {highlightText(mezzo.nomeMezzo || mezzo.nome, searchTerm)}
            </div>
            {mezzo.targa && (
              <div className="mezzo-targa">
                Targa: {highlightText(mezzo.targa, searchTerm)}
              </div>
            )}
          </div>
        </div>
      )}
      noResultsText="Nessun mezzo trovato per"
    />
  );
};

/**
 * Autocomplete per Materiali Variabili
 */
export const MaterialiVariabiliAutocomplete = ({
  materialiVariabili = [],
  selectedMateriale,
  setSelectedMateriale,
  onSelect,
  placeholder = "Cerca materiale...",
  label = "Materiale Variabile",
  required = false
}) => {
  return (
    <AutocompleteBase
      items={materialiVariabili}
      value={selectedMateriale}
      onSelect={(materiale) => {
        if (setSelectedMateriale) setSelectedMateriale(materiale?.id || null);
        if (onSelect) onSelect(materiale);
      }}
      placeholder={placeholder}
      label={label}
      required={required}
      displayKey="nomeMacrovoce"
      searchKeys={['nomeMacrovoce', 'nome']}
      renderSelectedItem={(materiale) => materiale.nomeMacrovoce || materiale.nome}
      renderItem={(materiale, searchTerm) => (
        <div className="materiale-item">
          <div className="materiale-icon">📦</div>
          <div className="materiale-info">
            <div className="materiale-name">
              {highlightText(materiale.nomeMacrovoce || materiale.nome, searchTerm)}
            </div>
            {materiale.prezzo && (
              <div className="materiale-price">
                €{materiale.prezzo}/ton
              </div>
            )}
          </div>
        </div>
      )}
      noResultsText="Nessun materiale trovato per"
    />
  );
};

/**
 * Autocomplete per Materiali Fissi
 */
export const MaterialiFissiAutocomplete = ({
  materialiFissi = [],
  selectedMateriale,
  setSelectedMateriale,
  onSelect,
  placeholder = "Cerca materiale fisso...",
  label = "Materiale Fisso",
  required = false
}) => {
  return (
    <AutocompleteBase
      items={materialiFissi}
      value={selectedMateriale}
      onSelect={(materiale) => {
        if (setSelectedMateriale) setSelectedMateriale(materiale?.id || null);
        if (onSelect) onSelect(materiale);
      }}
      placeholder={placeholder}
      label={label}
      required={required}
      displayKey="nomeMacrovoce"
      searchKeys={['nomeMacrovoce', 'nome']}
      renderSelectedItem={(materiale) => materiale.nomeMacrovoce || materiale.nome}
      renderItem={(materiale, searchTerm) => (
        <div className="materiale-item">
          <div className="materiale-icon">🏗️</div>
          <div className="materiale-info">
            <div className="materiale-name">
              {highlightText(materiale.nomeMacrovoce || materiale.nome, searchTerm)}
            </div>
            {materiale.prezzo && (
              <div className="materiale-price">
                €{materiale.prezzo}/ton
              </div>
            )}
          </div>
        </div>
      )}
      noResultsText="Nessun materiale trovato per"
    />
  );
};

/**
 * Autocomplete per Lavorazioni
 */
export const LavorazioniAutocomplete = ({
  lavorazioni = [],
  selectedLavorazione,
  setSelectedLavorazione,
  onSelect,
  placeholder = "Cerca lavorazione...",
  label = "Lavorazione",
  required = false
}) => {
  return (
    <AutocompleteBase
      items={lavorazioni}
      value={selectedLavorazione}
      onSelect={(lavorazione) => {
        if (setSelectedLavorazione) setSelectedLavorazione(lavorazione?.id || null);
        if (onSelect) onSelect(lavorazione);
      }}
      placeholder={placeholder}
      label={label}
      required={required}
      displayKey="nomeLavorazione"
      searchKeys={['nomeLavorazione', 'nome']}
      renderSelectedItem={(lavorazione) => lavorazione.nomeLavorazione || lavorazione.nome}
      renderItem={(lavorazione, searchTerm) => (
        <div className="lavorazione-item">
          <div className="lavorazione-icon">⚙️</div>
          <div className="lavorazione-info">
            <div className="lavorazione-name">
              {highlightText(lavorazione.nomeLavorazione || lavorazione.nome, searchTerm)}
            </div>
          </div>
        </div>
      )}
      noResultsText="Nessuna lavorazione trovata per"
    />
  );
};

// Export default per componente base
export default AutocompleteBase;